import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatNumber } from '@/utils/formatters';

/**
 * Genera un PDF del índice de precios con imágenes
 * @param {Array} quoteTypes - Lista de tipos de cotización
 * @param {Object} stats - Estadísticas generales (total tipos, promedio, área)
 */
export const generatePriceIndexPDF = async (quoteTypes, stats) => {
  // Create PDF in landscape for better table layout
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  // Header
  doc.setFontSize(22);
  doc.setTextColor(30, 41, 59); // slate-800
  doc.text('AluMetric - Índice de Precios', margin, 20);

  // Subtitle with date
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139); // slate-500
  const dateStr = new Date().toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  doc.text(`Generado: ${dateStr}`, margin, 27);

  // Stats summary boxes
  const statsY = 35;
  const boxWidth = 55;
  const boxHeight = 18;
  const boxGap = 10;

  // Box 1: Total types
  doc.setFillColor(241, 245, 249); // slate-100
  doc.roundedRect(margin, statsY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text('TIPOS INDEXADOS', margin + 4, statsY + 6);
  doc.setFontSize(14);
  doc.setTextColor(30, 41, 59);
  doc.text(String(stats.totalTypes || quoteTypes.length), margin + 4, statsY + 14);

  // Box 2: Average price
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(margin + boxWidth + boxGap, statsY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text('PROMEDIO GENERAL', margin + boxWidth + boxGap + 4, statsY + 6);
  doc.setFontSize(14);
  doc.setTextColor(30, 41, 59);
  doc.text(formatCurrency(stats.averagePrice || 0) + '/m²', margin + boxWidth + boxGap + 4, statsY + 14);

  // Box 3: Total area
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(margin + (boxWidth + boxGap) * 2, statsY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text('ÁREA TOTAL', margin + (boxWidth + boxGap) * 2 + 4, statsY + 6);
  doc.setFontSize(14);
  doc.setTextColor(30, 41, 59);
  doc.text(formatNumber(stats.totalArea || 0) + ' m²', margin + (boxWidth + boxGap) * 2 + 4, statsY + 14);

  // Box 4: Total samples
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(margin + (boxWidth + boxGap) * 3, statsY, boxWidth, boxHeight, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text('MUESTRAS', margin + (boxWidth + boxGap) * 3 + 4, statsY + 6);
  doc.setFontSize(14);
  doc.setTextColor(30, 41, 59);
  doc.text(String(stats.totalSamples || 0), margin + (boxWidth + boxGap) * 3 + 4, statsY + 14);

  // Prepare table data with images
  const tableData = await Promise.all(quoteTypes.map(async (type, index) => {
    // Convert base64 image to format jsPDF can use
    let imageData = null;
    if (type.image_data) {
      try {
        imageData = type.image_data;
      } catch (e) {
        console.warn("Error loading image for PDF export:", e?.message || e);
      }
    }

    return {
      index: index + 1,
      image: imageData,
      name: type.name || 'Sin nombre',
      material: type.material || '-',
      series: type.series || '-',
      color: type.color || '-',
      factory: type.factory_name || '-',
      samples: type.entries_count || 0,
      area: formatNumber(type.total_area || 0),
      avgPrice: formatCurrency(type.average_cost_per_m2 || 0),
      minPrice: formatCurrency(type.min_cost_per_m2 || 0),
      maxPrice: formatCurrency(type.max_cost_per_m2 || 0)
    };
  }));

  // Generate table with autoTable
  autoTable(doc, {
    startY: statsY + boxHeight + 10,
    head: [[
      '#',
      'Imagen',
      'Nombre',
      'Material',
      'Fábrica',
      'Muestras',
      'Área (m²)',
      'Prom./m²',
      'Mín./m²',
      'Máx./m²'
    ]],
    body: tableData.map(row => [
      row.index,
      '', // Placeholder for image
      row.name,
      row.material,
      row.factory,
      row.samples,
      row.area,
      row.avgPrice,
      row.minPrice,
      row.maxPrice
    ]),
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: [226, 232, 240], // slate-200
      lineWidth: 0.1,
      textColor: [30, 41, 59]
    },
    headStyles: {
      fillColor: [241, 245, 249], // slate-100
      textColor: [71, 85, 105], // slate-600
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { halign: 'center', cellWidth: 18 },
      2: { cellWidth: 50 },
      3: { halign: 'center', cellWidth: 18 },
      4: { halign: 'center', cellWidth: 28 },
      5: { halign: 'center', cellWidth: 15 },
      6: { halign: 'right', cellWidth: 18 },
      7: { halign: 'right', cellWidth: 22, fontStyle: 'bold' },
      8: { halign: 'right', cellWidth: 20 },
      9: { halign: 'right', cellWidth: 20 }
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252] // slate-50
    },
    didDrawCell: (data) => {
      // Draw images in the image column
      if (data.section === 'body' && data.column.index === 1) {
        const rowData = tableData[data.row.index];
        if (rowData && rowData.image) {
          try {
            const imgSize = 12;
            const x = data.cell.x + (data.cell.width - imgSize) / 2;
            const y = data.cell.y + (data.cell.height - imgSize) / 2;
            doc.addImage(rowData.image, 'PNG', x, y, imgSize, imgSize);
          } catch (e) {
            // If image fails, draw a placeholder
            doc.setFillColor(241, 245, 249);
            doc.rect(
              data.cell.x + 2,
              data.cell.y + 2,
              data.cell.width - 4,
              data.cell.height - 4,
              'F'
            );
          }
        } else {
          // No image placeholder
          doc.setFillColor(241, 245, 249);
          const size = 12;
          const x = data.cell.x + (data.cell.width - size) / 2;
          const y = data.cell.y + (data.cell.height - size) / 2;
          doc.roundedRect(x, y, size, size, 1, 1, 'F');
        }
      }
    },
    didDrawPage: (data) => {
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184); // slate-400
      doc.text(
        `Página ${data.pageNumber} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.text(
        'AluMetric - Sistema de Índice de Precios',
        margin,
        pageHeight - 10
      );
    }
  });

  // Save the PDF
  const fileName = `AluMetric_Indice_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);

  return fileName;
};

export default generatePriceIndexPDF;
